import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Box,
} from '@mui/material';

const AddConditionDialog = ({ open, onClose, onAdd, availableConditions, getCutoffConditionDisplayName, analyteLookupOptions }) => {
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [selectedPrimaryAnalytes, setSelectedPrimaryAnalytes] = useState([]);
  const [primaryAnalyteTouched, setPrimaryAnalyteTouched] = useState(false);

  const selectedPrimaryAnalyteOptions = (analyteLookupOptions || []).filter(
    (opt) => selectedPrimaryAnalytes.includes(opt.value)
  );

  const showPrimaryAnalyteError = primaryAnalyteTouched && selectedPrimaryAnalytes.length === 0;

  const handleClose = () => {
    setSelectedCondition(null);
    setSelectedPrimaryAnalytes([]);
    setPrimaryAnalyteTouched(false);
    onClose();
  };

  const handleAdd = () => {
    if (selectedCondition && selectedPrimaryAnalytes.length > 0) {
      const primaryAnalyte = selectedPrimaryAnalytes.join(',');
      onAdd({ ...selectedCondition, primaryAnalyte });
      setSelectedCondition(null);
      setSelectedPrimaryAnalytes([]);
      setPrimaryAnalyteTouched(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="add-condition-dialog-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="add-condition-dialog-title" sx={{ textAlign: 'center' }}>Add Analyte Cutoff Condition</DialogTitle>
      <DialogContent>
        <Box sx={{ minHeight: 180 }}>
          <Autocomplete
            sx={{ mt: 1 }}
            options={availableConditions || []}
            getOptionLabel={(option) => getCutoffConditionDisplayName(option)}
            value={selectedCondition}
            onChange={(event, newValue) => {
              setSelectedCondition(newValue);
              setSelectedPrimaryAnalytes([]);
              setPrimaryAnalyteTouched(false);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Condition"
                variant="outlined"
                size="small"
                required
              />
            )}
            isOptionEqualToValue={(option, value) => {
              const optKey = option.orphanetCode || option.acronym || option.name;
              const valKey = value.orphanetCode || value.acronym || value.name;
              return optKey === valKey;
            }}
          />

          {selectedCondition && (
            <Autocomplete
              sx={{ mt: 2 }}
              size="small"
              multiple
              id="primaryAnalytes"
              options={analyteLookupOptions || []}
              value={selectedPrimaryAnalyteOptions}
              onChange={(event, newValue) => {
                setSelectedPrimaryAnalytes(newValue.map((opt) => opt.value));
                setPrimaryAnalyteTouched(true);
              }}
              getOptionLabel={(option) => option?.description ? `${option.description} (${option.label})` : (option?.label || '')}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              filterSelectedOptions
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Primary Analyte(s)"
                  variant="outlined"
                  size="small"
                  required
                  error={showPrimaryAnalyteError}
                  helperText={showPrimaryAnalyteError ? 'At least one primary analyte is required.' : ''}
                />
              )}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleAdd}
          variant="contained"
          color="primary"
          disabled={!selectedCondition || selectedPrimaryAnalytes.length === 0}
        >
          Add
        </Button>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddConditionDialog;

